import { Box, Input } from "@chakra-ui/react";
import { format as formatDate } from "date-fns";
import { nb } from "date-fns/locale/nb";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DateSelectorCustomPicker.css";
import { DatePickerHeader } from "./DatePickerHeader";

registerLocale("nb", nb);
setDefaultLocale("nb");

interface ThemedDatePickerProps {
  id?: string;
  defaultDate?: string;
  date?: string | null;
  onChange: (date?: string) => Promise<void> | void;
  onBlur?: () => Promise<void> | void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  allowPastDates?: boolean;
  minDate?: Date;
}

const CustomInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>(
  (props, ref) => (
    <Input
      ref={ref}
      {...props}
      fontSize="md"
      maxWidth={{ sm: "full", md: "60" }}
      border="1px solid"
      borderColor="smBorder"
      backgroundColor="smBackground"
      rounded="md"
      p="4"
      readOnly
      cursor="pointer"
    />
  )
);
CustomInput.displayName = "CustomInput";

const parseToDate = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const PORTAL_ID = "datepicker-portal";

const localeMap: Record<string, string> = { no: "nb-NO", en: "en-US" };

export const ThemedDatePicker = ({
  id,
  defaultDate,
  date,
  onChange,
  onBlur,
  isDisabled,
  autoFocus = false,
  placeholder,
  allowPastDates,
  minDate,
}: ThemedDatePickerProps) => {
  const { i18n } = useTranslation();
  const intlLocale = localeMap[i18n.language] ?? "nb-NO";
  const [internalDate, setInternalDate] = useState<Date | null>(parseToDate(defaultDate));
  const selected = date !== undefined ? parseToDate(date) : internalDate;

  useEffect(() => {
    if (!document.getElementById(PORTAL_ID)) {
      const el = document.createElement("div");
      el.id = PORTAL_ID;
      document.body.appendChild(el);
    }
  }, []);

  const handleChange = (d: Date | null) => {
    setInternalDate(d);
    onChange(d ? formatDate(d, "yyyy-MM-dd") : undefined);
  };

  return (
    <Box sx={{ ".react-datepicker-wrapper": { maxWidth: { sm: "full", md: "60" } } }}>
      <DatePicker
        id={id}
        selected={selected}
        onChange={handleChange}
        onCalendarClose={onBlur}
        autoFocus={autoFocus}
        dateFormat="dd.MM.yyyy"
        placeholderText={placeholder ?? "dd.mm.yyyy"}
        customInput={<CustomInput />}
        disabled={isDisabled}
        minDate={allowPastDates ? undefined : (minDate ?? new Date())}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        portalId={PORTAL_ID}
        renderCustomHeader={(props) => <DatePickerHeader {...props} locale={intlLocale} />}
      />
    </Box>
  );
};
